import { ControlStatus } from "@oneleet/api_client";
import { cn, getReadableText } from "@oneleet/utils";

import { VariantProps, cva } from "class-variance-authority";

// eslint-disable-next-line react-refresh/only-export-components
export const controlColorMapping = {
  [ControlStatus.PASSING]: "stroke-ng-green-900 fill-ng-green-800",
  [ControlStatus.FAILING]: "stroke-ng-red-900 fill-ng-red-800",
  [ControlStatus.NEEDS_CHANGES]: "stroke-ng-orange-900 fill-ng-orange-400",
  [ControlStatus.IN_REVIEW]: "stroke-ng-green-900 fill-ng-green-400",
  [ControlStatus.IN_PROGRESS]: "fill-accent-400 stroke-accent-900",
  [ControlStatus.NOT_STARTED]: "fill-neutral-50 stroke-neutral-400",
};

const statusIndicatorVariants = cva("shrink-0", {
  variants: {
    status: {
      ...controlColorMapping,
      disabled: "",
    },
    size: {
      sm: "size-2",
      md: "size-2.5",
      lg: "size-3",
      xl: "size-3.5",
    },
  },
  defaultVariants: {
    size: "md",
  },
});

type ControlStatusIndicatorProps = VariantProps<
  typeof statusIndicatorVariants
> & {
  className?: string;
};

export const ControlStatusIndicator = ({
  status,
  size,
  className,
}: ControlStatusIndicatorProps) => {
  if (!status)
    throw new Error(
      "No status prop was passed to ControlStatusIndicator but is required",
    );

  if (status === "disabled") {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 12 12"
        fill="none"
        className={cn(statusIndicatorVariants({ size }), className)}
        aria-label="Disabled control"
      >
        <path
          d="M2.7373 9.91963C2.53692 9.91963 2.34715 9.87446 2.17755 9.79345L9.91112 2.05987C9.99214 2.22948 10.0373 2.41925 10.0373 2.61963V8.61963C10.0373 9.3376 9.45527 9.91963 8.7373 9.91963H2.7373ZM2.7373 1.31963H6.55015L1.4373 6.43247V2.61963C1.4373 1.90166 2.01933 1.31963 2.7373 1.31963Z"
          fill="#D6CFCD"
          stroke="#A09D9C"
          strokeWidth="1.4"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M11.6564 0.343205C11.9493 0.636098 11.9493 1.11097 11.6564 1.40387L1.40338 11.6569C1.11048 11.9498 0.63561 11.9498 0.342717 11.6569C0.0498236 11.364 0.0498236 10.8891 0.342717 10.5963L10.5958 0.343205C10.8887 0.0503118 11.3635 0.0503118 11.6564 0.343205Z"
          fill="#A09D9C"
        />
      </svg>
    );
  }

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 12 12"
      fill="none"
      aria-label={
        getReadableText(status, { caseType: "sentenceCase" }) + " control"
      }
      className={cn(statusIndicatorVariants({ status, size }), className)}
    >
      <rect
        x="0.7"
        y="0.7"
        width="10.6"
        height="10.6"
        rx="1.3"
        strokeWidth="1.4"
      />
    </svg>
  );
};
